import { SESSION_ACCOUNT_DEACTIVATED, sessionStateResponse } from '@cms/shared';

/**
 * The thirty-second poll (docs/SINGLE-SESSION-PLAN.md §4c, D-1).
 *
 * ── What problem is left for this to solve ────────────────────────────────────────────
 * Slice 5 told a superseded session why it ended, but only when it next tried something: a refresh
 * (up to fifteen minutes away) or a request that 401s. A tab sitting open on a form does neither, so
 * somebody could keep typing into a session that no longer exists for a quarter of an hour. This
 * bounds that at one interval.
 *
 * D-1 chose it over 4b — a session check inside the auth middleware — because 4b makes the guarantee
 * airtight at the cost of a database read on every authenticated request, which the middleware has
 * deliberately never had. One cheap request per client per thirty seconds buys most of the benefit
 * and changes nothing about the shape of the app.
 *
 * ── Why the poll can only ever be late, never wrong ───────────────────────────────────
 * Every failure is ignored. A network blip, a 429, a 500, a body that does not parse, an aborted
 * request during teardown: all of them mean "no information", never "you are signed out". The server
 * half is written the same way (see `sessionState`), and the reasoning is the same — a missed
 * supersession costs minutes and is caught by the refresh path anyway, while a false positive signs a
 * working session out mid-sentence.
 *
 * So the session ends on exactly one answer: a 200 that parses and says `current: false`.
 *
 * ── Two more things it can learn ──────────────────────────────────────────────────────
 * Why it ended — signed in elsewhere, or the account was deactivated — and that the account's
 * permissions changed under a live session, which the app answers by refreshing at once rather than
 * carrying the old permissions until the access token expires.
 */
export const SESSION_POLL_INTERVAL_MS = 30_000;

/** Why a session ended, as far as the person at the keyboard needs to know. */
export type SessionEndReason = 'elsewhere' | 'deactivated';

/** What one poll concluded. `null` is "nothing to do", and every kind of failure. */
export type SessionVerdict = { ended: SessionEndReason } | { refreshAccess: true } | null;

/** Asks once. */
export async function pollSession(signal?: AbortSignal): Promise<SessionVerdict> {
  try {
    const response = await fetch('/api/v1/auth/session', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    });

    if (!response.ok) return null;

    const parsed = sessionStateResponse.safeParse(await response.json());
    if (!parsed.success) return null;
    if (!parsed.data.current) {
      return {
        ended: parsed.data.reason === SESSION_ACCOUNT_DEACTIVATED ? 'deactivated' : 'elsewhere',
      };
    }
    return parsed.data.refreshAccess ? { refreshAccess: true } : null;
  } catch {
    // Includes the abort on unmount, which must not be read as a verdict.
    return null;
  }
}

/** The earlier question, kept for its callers: has this session ended? */
export async function pollSessionEnded(signal?: AbortSignal): Promise<boolean> {
  const verdict = await pollSession(signal);
  return verdict !== null && 'ended' in verdict;
}

/**
 * Runs the poll until the returned function is called, and calls `onEnded` at most once.
 *
 * ── Why it stops while the tab is hidden ──────────────────────────────────────────────
 * A background tab does not need to learn this within thirty seconds — nobody is typing into it — and
 * every client polling around the clock is the cost that makes a poll worth questioning. It asks
 * immediately when the tab becomes visible instead, which is the moment that actually matters: the
 * old tab someone comes back to should not be the last thing to find out.
 *
 * `onEnded` is called once and then the poll stops. Signing out is not a thing to do twice, and the
 * cookie is still on the browser until the server clears it, so a second answer would be the same
 * answer. `onAccessChanged` may be called on any poll; the poll carries on.
 */
export function startSessionPoll(
  onEnded: (reason: SessionEndReason) => void,
  intervalMs: number = SESSION_POLL_INTERVAL_MS,
  onAccessChanged?: () => void | Promise<void>,
): () => void {
  const controller = new AbortController();
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    controller.abort();
  };

  const ask = async () => {
    if (stopped || document.visibilityState === 'hidden') return;
    const verdict = await pollSession(controller.signal);
    if (stopped || verdict === null) return;
    if ('ended' in verdict) {
      stop();
      onEnded(verdict.ended);
      return;
    }
    await onAccessChanged?.();
  };

  const onVisible = () => {
    if (document.visibilityState === 'visible') void ask();
  };

  const timer = setInterval(() => void ask(), intervalMs);
  document.addEventListener('visibilitychange', onVisible);

  return stop;
}
